import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const BASE_URL = 'https://momentsent.com';
const PATH = '/what-to-write-in-a-sympathy-card';

/**
 * Message-ideas guide: /what-to-write-in-a-sympathy-card
 *
 * A genuinely useful, citable resource (GEO play): answer-first intro, then
 * tasteful ready-to-use sympathy messages grouped by relationship, plus
 * guidance on what to avoid. The tone here is gentle and respectful, not
 * promotional. The closing note about mailing a card is soft and brief.
 * Emits Article + FAQPage + BreadcrumbList JSON-LD.
 */

interface MessageGroup {
  heading: string;
  intro:   string;
  messages: string[];
}

const GROUPS: MessageGroup[] = [
  {
    heading: 'Short and sincere',
    intro:   'When words feel hard to find, a few honest ones are enough.',
    messages: [
      'I am so sorry for your loss. Thinking of you with love.',
      'There are no words for a loss like this. Please know I am here for you.',
      'Holding you close in my heart during this difficult time.',
      'Sending you my deepest sympathy and a great deal of love.',
      'My heart is with you and your family. I am so very sorry.',
      'Thinking of you and wishing you comfort and peace in the days ahead.',
    ],
  },
  {
    heading: 'Offering support',
    intro:   'Let them know they are not alone and that your offer of help is real.',
    messages: [
      'Please lean on me however you need to. I am only a phone call away.',
      'You do not have to go through this alone. I am here for whatever you need, whenever you need it.',
      'I cannot take the pain away, but I can sit with you in it. I am here.',
      'In the days and weeks ahead, please let me help, with a meal, an errand, or just company.',
      'There is no right way to grieve and no timeline. Take all the time you need, and lean on me along the way.',
    ],
  },
  {
    heading: 'Honoring the person who passed',
    intro:   'A specific memory or quality can be a great comfort. Share what you loved about them.',
    messages: [
      'Your mother had a way of making everyone feel welcome. I will always remember her warmth.',
      'I will never forget your father’s kindness and his wonderful sense of humor. He touched so many lives.',
      'The world was brighter for having known them. Their memory will live on in all of us.',
      'They left a mark on everyone lucky enough to know them, and that does not fade.',
      'I feel grateful to have known someone so generous and so loved. They will be deeply missed.',
    ],
  },
  {
    heading: 'For the loss of a spouse or partner',
    intro:   'Acknowledge the depth of the bond with gentleness.',
    messages: [
      'The love you shared was rare and beautiful. I am holding you close in my heart.',
      'There are no words for the loss of a partner. Please know how deeply I am thinking of you.',
      'Your love story touched everyone around you. Sending you strength and so much love.',
      'I am so sorry for the loss of your beloved. May the love you shared bring you comfort in time.',
    ],
  },
  {
    heading: 'Words of comfort and peace',
    intro:   'Gentle, faith-neutral wishes that suit almost anyone.',
    messages: [
      'May the love of those around you carry you through the days ahead.',
      'May your memories bring you comfort, and may you feel surrounded by love.',
      'Wishing you peace, comfort, and the gentle presence of everyone who loves you.',
      'May you find moments of peace in the midst of your grief, and strength in the love around you.',
      'Sending you quiet strength and a great deal of love during this difficult time.',
    ],
  },
];

const FAQ = [
  {
    q: 'What should I write in a sympathy card?',
    a: 'Keep it simple, sincere, and warm. Acknowledge the loss, share a kind word or a brief memory of the person who passed, and offer your support. For example: "I am so sorry for your loss. Your mother had a way of making everyone feel welcome, and I will always remember her warmth. Please lean on me however you need to." A short, heartfelt note is always enough. Close with a gentle sign-off and your name.',
  },
  {
    q: 'What is a good short sympathy message?',
    a: 'A good short sympathy message is gentle and sincere, for example: "I am so sorry for your loss. Thinking of you with love." or "Holding you close in my heart during this difficult time." When words are hard to find, a few honest ones mean a great deal.',
  },
  {
    q: 'What should you not write in a sympathy card?',
    a: 'Avoid phrases that minimize the loss or rush the grief, such as "everything happens for a reason," "they are in a better place," "I know exactly how you feel," or "at least." Avoid talking about yourself, and avoid asking how you can help in a way that puts work on the grieving person. Instead, offer specific support and let them know you are there.',
  },
  {
    q: 'How do I sign off a sympathy card?',
    a: 'Use a gentle closer that fits your relationship, such as "With deepest sympathy," "With love," "Thinking of you," or "With heartfelt condolences," then your name. Keep the sign-off warm and understated.',
  },
];

export default function WhatToWriteSympathyPage() {
  const canonical = `${BASE_URL}${PATH}`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'What to Write in a Sympathy Card: Comforting Message Ideas',
      description: 'Tasteful, ready-to-use sympathy card messages grouped by relationship, plus gentle guidance on what to write, what to avoid, and how to sign off.',
      author: { '@type': 'Organization', name: 'MomentSent' },
      publisher: {
        '@type': 'Organization',
        name: 'MomentSent',
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo-wordmark.png` },
      },
      mainEntityOfPage: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'What to Write in a Sympathy Card', item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="What to Write in a Sympathy Card: Comforting Message Ideas | MomentSent"
        description="Not sure what to write in a sympathy card? Use these gentle, ready-to-go condolence messages grouped by relationship, plus what to write, what to avoid, and how to sign off."
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero / answer-first intro ───────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Message ideas
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            What to write in a sympathy card
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            The best sympathy card messages are simple, sincere, and warm. Acknowledge the loss,
            share a kind word or a brief memory of the person who passed, and offer your support.
            You do not need the perfect words. A few honest ones are always enough. Below are gentle,
            ready-to-use messages grouped by relationship, along with what to avoid and how to sign off.
          </p>
        </div>
      </section>

      {/* ── Message groups ──────────────────────────────────────────────────── */}
      <section className="pb-4 px-6">
        <div className="max-w-3xl mx-auto space-y-10">
          {GROUPS.map((g) => (
            <div key={g.heading}>
              <h2 className="font-heading text-2xl text-navy mb-2">{g.heading}</h2>
              <p className="font-body text-base text-gray-500 leading-relaxed mb-5">{g.intro}</p>
              <ul className="space-y-3">
                {g.messages.map((m) => (
                  <li
                    key={m}
                    className="bg-white rounded-card p-5 shadow-warm border border-gray-100
                               font-body text-base text-gray-700 leading-relaxed"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── What to avoid ───────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">What to avoid writing</h2>
          <ul className="space-y-4">
            {[
              'Avoid phrases that minimize the loss, like "everything happens for a reason" or "at least they lived a long life."',
              'Avoid rushing the grief. Skip "time heals all wounds" or anything that suggests they should move on.',
              'Avoid "I know exactly how you feel." Every loss is different, even if you have grieved before.',
              'Avoid making it about you. Keep the focus on them and the person they lost.',
              'Avoid vague offers like "let me know if you need anything." Offer something specific instead.',
            ].map((t) => (
              <li key={t} className="flex gap-3">
                <span className="text-sage font-bold mt-0.5" aria-hidden="true">•</span>
                <span className="font-body text-base text-gray-600 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl text-navy mb-6">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg text-navy mb-2">{f.q}</h3>
                <p className="font-body text-base text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Soft CTA ────────────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-2xl text-navy mb-4">
            A real card means a great deal.
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            When you are ready to send one, MomentSent can print and mail a real, physical card for
            you, with postage included, so a heartfelt note reaches them even from far away.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Get started →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
